/* eslint-disable object-shorthand */
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import {
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  Grid,
  IconButton,
  makeStyles,
  Card,
  LinearProgress,
} from "@material-ui/core";
import { Search, Clear } from "@material-ui/icons";
import moment from "moment";
import { Pagination } from "@material-ui/lab";
import { useAuth } from "../../../../contexts/AuthContext";
import PeriodSelector from "../../../global/PeriodSelector";
import AgentReport from "./shared/agent-report/agent-report.shared";
import * as API from "../../../../api";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0.5, 0.5, 0),
    justifyContent: "space-between",
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  textField: {
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
    margin: theme.spacing(1, 0.5, 1.5),
    "& .MuiSvgIcon-root": {
      marginRight: theme.spacing(0.5),
    },
    "& .MuiInput-underline:before": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  filterContainer: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  searchCard: {
    marginBottom: theme.spacing(2),
    overflow: "visible",
  },
  paginationContainer: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  performanceInfo: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
}));

/**
 * FibrePeriodicOrderReport component displays a report of fibre orders
 * within a specified date range for a given dealer code and supplier.
 * It shows the number of orders created, paid, installed, and activated,
 * and allows for impersonation to view another user's dealer code.
 * The report data is fetched from the server based on the current
 * user's dealer code, or an impersonated dealer code if provided.
 *
 * Now includes search functionality for historical orders with pagination.
 *
 * @param {object} props - The component props.
 * @param {string} props.supplierPrettyName - The supplier's pretty name for display purposes.
 * @param {boolean} props.impersonation - Whether the component is being impersonated.
 * @param {string} props.impersonationName - The name of the impersonated user.
 */
export default function FibrePeriodicOrderReport({
  supplierPrettyName,
  impersonation,
  impersonationName,
}) {
  const classes = useStyles();

  // Retrieve the current user from the authentication context
  const { currentUser, authSession } = useAuth();

  // Get supplierId from params
  const { supplierId } = useParams();

  // Set the initial dealer code to the current user's dealer code
  const [dealerCode, setDealerCode] = useState(currentUser.id);

  // Set the initial parameters, loading state, data, and customer modal state
  const [showLoading, setShowLoading] = useState(false);
  const [data, setData] = useState([]);

  // Set the initial start and end dates to the current month
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );

  // Search related state
  const [localSearchValue, setLocalSearchValue] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 50,
  });

  // Tile values
  const [ordersCreated, setOrdersCreated] = useState(0);
  const [ordersPaid, setOrdersPaid] = useState(0);
  const [ordersInstalled, setOrdersInstalled] = useState(0);
  const [ordersActivated, setOrdersActivated] = useState(0);

  const tiles = [
    {
      label: "Orders Created",
      value: ordersCreated,
    },
    {
      label: "Orders Paid",
      value: ordersPaid,
    },
    {
      label: "Orders Installed",
      value: ordersInstalled,
    },
    {
      label: "Orders Activated",
      value: ordersActivated,
    },
  ];

  /**
   * Performs a search for orders using the given search value and page number.
   * Only performs the search if the search value is at least 2 characters long.
   * If the search is successful, updates the search results and pagination state.
   * If the search fails, logs an error to the console and resets the search results and pagination state.
   * @param {string} searchValue - The search value to use.
   * @param {number} [page=1] - The page number to fetch.
   */
  const performSearch = async (searchValue, page = 1) => {
    if (!searchValue || searchValue.length < 2) return;

    setSearchLoading(true);
    try {
      const { data: response, error } = await API.getOrderReport({
        user_id: dealerCode,
        supplier_id: supplierId,
        access_token: authSession.access_token,
        report: "order",
        search: searchValue,
        page: page,
        limit: 50,
      });

      if (error) {
        console.error("Search error:", error);
        return;
      }

      setSearchResults(response.data || []);
      setSearchPagination(
        response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 50,
        }
      );
      setIsSearchActive(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setSearchPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 50,
      });
    } finally {
      setSearchLoading(false);
    }
  };

  /**
   * Handle search execution (triggered by button click or Enter key)
   */
  const handleSearchClick = useCallback(() => {
    const trimmedValue = localSearchValue.trim();
    if (trimmedValue.length >= 2) {
      performSearch(trimmedValue, 1);
    } else if (trimmedValue.length === 0) {
      clearSearch();
    }
  }, [localSearchValue, dealerCode, supplierId, authSession.access_token]);

  /**
   * Handle Enter key press in search input
   */
  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter") {
        handleSearchClick();
      }
    },
    [handleSearchClick]
  );

  /**
   * Handle search input change (no API call)
   */
  const handleSearchChange = useCallback(event => {
    setLocalSearchValue(event.target.value);
  }, []);

  /**
   * Clears the search and returns to period-based results
   */
  const clearSearch = useCallback(() => {
    setLocalSearchValue("");
    setIsSearchActive(false);
    setSearchResults([]);
    setSearchPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasNextPage: false,
      hasPrevPage: false,
      limit: 50,
    });
    // Reload the original period data
    getData();
  }, []);

  /**
   * Clear individual search input
   */
  const clearSearchInput = useCallback(() => {
    setLocalSearchValue("");
    clearSearch();
  }, [clearSearch]);

  const handleSearchPageChange = (event, page) => {
    if (localSearchValue.trim().length >= 2) {
      performSearch(localSearchValue.trim(), page);
    }
  };

  /**
   * Fetches the data from the server.
   * @returns {Promise<void>} - A promise that resolves when the data is fetched.
   */
  const getData = async () => {
    setOrdersActivated(0);
    setOrdersCreated(0);
    setOrdersInstalled(0);
    setOrdersPaid(0);
    setShowLoading(true);
    // Call the server API to get the agent report
    const { data: response, error } = await API.getOrderReport({
      user_id: dealerCode,
      supplier_id: supplierId,
      start_date: startDate,
      end_date: endDate,
      access_token: authSession.access_token,
      report: "order",
    });
    if (error) console.error(error);

    // Handle both old and new response formats
    const orderData = response?.data || response;
    setData(orderData);
    setShowLoading(false);
  };

  // Fetch data when the dealer code, start date, or end date changes
  useEffect(() => {
    if (dealerCode && startDate && endDate && supplierId && !isSearchActive) {
      getData();
    }
  }, [dealerCode, startDate, endDate, supplierId, isSearchActive]);

  // Update the dealer code when the impersonation changes or when the component mounts
  useEffect(() => {
    if (impersonation) {
      setDealerCode(impersonation);
    } else {
      setDealerCode(currentUser.id);
    }
  }, [impersonation]);

  useEffect(() => {
    const currentData = isSearchActive ? searchResults : data;

    if (currentData && currentData.length > 0) {
      // For search results, we don't filter by date range since search covers last year
      if (isSearchActive) {
        // calculate and set orders created
        const ordersCreatedCount = currentData?.filter(
          item => item.order_created_date && item.order_created_date.length > 0
        ).length;

        setOrdersCreated(ordersCreatedCount);

        // calculate and set orders paid
        const ordersPaidCount = currentData?.filter(
          item => item.order_paid_date && item.order_paid_date.length > 0
        ).length;

        setOrdersPaid(ordersPaidCount);

        // calculate and set orders installed
        const ordersInstalledCount = currentData?.filter(
          item =>
            item.order_installation_date &&
            item.order_installation_date.length > 0
        ).length;

        setOrdersInstalled(ordersInstalledCount);

        // calculate and set orders activated
        const ordersActivatedCount = currentData?.filter(
          item =>
            item.order_activation_date && item.order_activation_date.length > 0
        ).length;

        setOrdersActivated(ordersActivatedCount);
      } else {
        // Original logic for period-based results
        // calculate and set orders created
        const ordersCreatedCount = currentData?.filter(
          item =>
            item.order_created_date &&
            item.order_created_date.length > 0 &&
            moment(item.order_created_date).isBetween(
              startDate,
              endDate,
              null,
              "[]"
            )
        ).length;

        setOrdersCreated(ordersCreatedCount);

        // calculate and set orders paid
        const ordersPaidCount = currentData?.filter(
          item =>
            item.order_paid_date &&
            item.order_paid_date.length > 0 &&
            moment(item.order_paid_date).isBetween(
              startDate,
              endDate,
              null,
              "[]"
            )
        ).length;

        setOrdersPaid(ordersPaidCount);

        // calculate and set orders installed
        const ordersInstalledCount = currentData?.filter(
          item =>
            item.order_installation_date &&
            item.order_installation_date.length > 0 &&
            moment(item.order_installation_date).isBetween(
              startDate,
              endDate,
              null,
              "[]"
            )
        ).length;

        setOrdersInstalled(ordersInstalledCount);

        // calculate and set orders activated
        const ordersActivatedCount = currentData?.filter(
          item =>
            item.order_activation_date &&
            item.order_activation_date.length > 0 &&
            moment(item.order_activation_date).isBetween(
              startDate,
              endDate,
              null,
              "[]"
            )
        ).length;

        setOrdersActivated(ordersActivatedCount);
      }
    } else {
      // Reset counts when no data
      setOrdersCreated(0);
      setOrdersPaid(0);
      setOrdersInstalled(0);
      setOrdersActivated(0);
    }
  }, [data, searchResults, isSearchActive, startDate, endDate]);

  return (
    <div>
      {/* Display the dealer code and impersonation name */}
      <Typography variant="h6">
        Activity Report for
        {impersonationName
          ? `: ${impersonationName}`
          : `: Z-${dealerCode} (You)`}
      </Typography>

      {/* Display the supplier name */}
      <Typography variant="h6">Service: {supplierPrettyName}</Typography>

      {/* Search Card */}
      <Card className={classes.searchCard}>
        <LinearProgress
          style={{ opacity: searchLoading ? 1 : 0, transition: "opacity .2s" }}
        />

        <Box className={classes.filterContainer}>
          <Typography variant="subtitle1" gutterBottom>
            Search Orders (Last Year):
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={9}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  variant="standard"
                  value={localSearchValue}
                  onChange={handleSearchChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Search by customer name, email, phone, address, account number, or order ID..."
                  className={classes.textField}
                  fullWidth
                  InputProps={{
                    startAdornment: <Search fontSize="small" />,
                    endAdornment: localSearchValue && (
                      <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        onClick={clearSearchInput}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSearchClick}
                  disabled={searchLoading}
                  style={{ minWidth: "80px", height: "36px" }}
                >
                  Search
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Tip: Enter at least 2 characters to search historical orders
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <Box className={classes.chipContainer}>
                {isSearchActive && (
                  <Chip
                    label={`Found: ${searchPagination.totalCount} orders`}
                    color="primary"
                    size="small"
                  />
                )}
                {isSearchActive && (
                  <Button
                    size="small"
                    onClick={clearSearch}
                    color="secondary"
                    variant="outlined"
                    style={{
                      fontSize: "0.75rem",
                      padding: "2px 8px",
                    }}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Search Pagination */}
      {isSearchActive && searchPagination.totalPages > 1 && (
        <Box className={classes.paginationContainer}>
          <Box>
            <Typography variant="caption" className={classes.performanceInfo}>
              Showing page {searchPagination.currentPage} of{" "}
              {searchPagination.totalPages}({searchPagination.totalCount} total
              results)
            </Typography>
            <Pagination
              count={searchPagination.totalPages}
              page={searchPagination.currentPage}
              onChange={handleSearchPageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      )}

      {/* Period Section - Only show when not searching */}
      {!isSearchActive && (
        <>
          <div>
            {/* Display the current period */}
            <p>
              Current Period:{" "}
              <b>
                {startDate} - {endDate}
              </b>
            </p>
          </div>

          {/* Render the period selector */}
          <PeriodSelector
            changeStartDate={date => setStartDate(date)}
            changeEndDate={date => setEndDate(date)}
          />
        </>
      )}

      {/* Results Section */}
      <AgentReport
        data={isSearchActive ? searchResults : data}
        loading={isSearchActive ? searchLoading : showLoading}
        tiles={tiles}
        impersonation={impersonation}
      />
    </div>
  );
}
